// frontend/src/api/samlApi.js

import { getBaseApiUrl } from '../utils/apiConfig.js';

const API_BASE_URL = getBaseApiUrl();

/**
 * SAML API service for handling SAML authentication and configuration
 */
class SAMLApi {
  /**
   * Get SAML configuration and status
   * @returns {Promise<Object>} SAML configuration
   */
  async getConfig() {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/saml/status`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to get SAML configuration');
    }

    return response.json();
  }

  /**
   * Initialize SAML provider
   * @param {Object} config - SAML configuration
   * @returns {Promise<Object>} Initialization result
   */
  async initialize(config = {}) {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/saml/initialize`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ config }),
    });

    if (!response.ok) {
      throw new Error('Failed to initialize SAML provider');
    }

    return response.json();
  }

  /**
   * Get Service Provider metadata
   * @returns {Promise<string>} SP metadata XML
   */
  async getServiceProviderMetadata() {
    const response = await fetch(`${API_BASE_URL}/api/auth/saml/metadata`);

    if (!response.ok) {
      throw new Error('Failed to get Service Provider metadata');
    }

    return response.text();
  }

  /**
   * Initiate SAML authentication
   * @param {string} idpName - Identity Provider name
   * @param {string} returnUrl - Return URL after authentication
   */
  initiateLogin(idpName, returnUrl = null) {
    const params = new URLSearchParams();
    if (returnUrl) {
      params.append('returnUrl', returnUrl);
    }

    const loginUrl = `${API_BASE_URL}/api/auth/saml/login/${encodeURIComponent(idpName)}${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    // Redirect to SAML login
    window.location.href = loginUrl;
  }

  /**
   * Initiate SAML logout
   * @param {string} idpName - Identity Provider name
   */
  initiateLogout(idpName) {
    const logoutUrl = `${API_BASE_URL}/api/auth/saml/logout/${encodeURIComponent(idpName)}`;

    // Redirect to SAML logout
    window.location.href = logoutUrl;
  }

  /**
   * Add or update Identity Provider configuration
   * @param {Object} idpConfig - IdP configuration
   * @returns {Promise<Object>} Result
   */
  async addIdentityProvider(idpConfig) {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/saml/idp`, {
      method: 'POST',
      headers,
      body: JSON.stringify(idpConfig),
    });

    if (!response.ok) {
      throw new Error('Failed to add Identity Provider');
    }

    return response.json();
  }

  /**
   * Test SAML configuration
   * @param {string} idpName - Identity Provider name
   * @returns {Promise<Object>} Test result
   */
  async testConfiguration(idpName) {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/saml/test`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ idp: idpName }),
    });

    if (!response.ok) {
      throw new Error('SAML configuration test failed');
    }

    return response.json();
  }

  /**
   * Parse SAML authentication result from URL
   * @returns {Object|null} Authentication result or null
   */
  parseAuthenticationResult() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const auth = params.get('auth');
    const error = params.get('error');

    if (error) {
      return {
        success: false,
        error: decodeURIComponent(error),
      };
    }

    if (token && auth === 'saml') {
      return {
        success: true,
        token,
        authMethod: 'saml',
      };
    }

    return null;
  }

  /**
   * Clear authentication result from URL
   */
  clearAuthenticationResult() {
    const url = new URL(window.location);
    url.searchParams.delete('token');
    url.searchParams.delete('auth');
    url.searchParams.delete('error');

    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Download Service Provider metadata as file
   */
  async downloadServiceProviderMetadata() {
    try {
      const metadata = await this.getServiceProviderMetadata();

      const blob = new Blob([metadata], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'virtualpat-sp-metadata.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download SP metadata:', error);
      throw error;
    }
  }

  /**
   * Validate IdP certificate
   * @param {string} certificate - X.509 certificate in PEM format
   * @returns {Promise<Object>} Validation result
   */
  async validateCertificate(certificate) {
    try {
      // Basic validation: check if it's a valid PEM format
      if (
        !certificate.includes('-----BEGIN CERTIFICATE-----') ||
        !certificate.includes('-----END CERTIFICATE-----')
      ) {
        throw new Error('Invalid certificate format. Expected PEM format.');
      }

      // Extract certificate content
      const certContent = certificate
        .replace('-----BEGIN CERTIFICATE-----', '')
        .replace('-----END CERTIFICATE-----', '')
        .replace(/\s/g, '');

      // Basic base64 validation
      try {
        atob(certContent);
      } catch (e) {
        throw new Error('Invalid certificate content. Not valid base64.');
      }

      return {
        valid: true,
        message: 'Certificate format is valid',
      };
    } catch (error) {
      return {
        valid: false,
        message: error.message,
      };
    }
  }

  /**
   * Get list of common Identity Provider templates
   * @returns {Array} List of IdP templates
   */
  getIdpTemplates() {
    return [
      {
        name: 'Azure AD',
        type: 'azure-ad',
        description: 'Microsoft Azure Active Directory',
        entityIdTemplate: 'https://sts.windows.net/{tenant-id}/',
        ssoServiceUrlTemplate: 'https://login.microsoftonline.com/{tenant-id}/saml2',
        sloServiceUrlTemplate: 'https://login.microsoftonline.com/{tenant-id}/saml2',
        attributeMapping: {
          userId: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
          email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
          firstName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
          lastName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
          role: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
        },
      },
      {
        name: 'AD FS',
        type: 'adfs',
        description: 'Active Directory Federation Services',
        entityIdTemplate: 'https://{adfs-server}/adfs/services/trust',
        ssoServiceUrlTemplate: 'https://{adfs-server}/adfs/ls/',
        sloServiceUrlTemplate: 'https://{adfs-server}/adfs/ls/',
        attributeMapping: {
          userId: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
          email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
          firstName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
          lastName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
          role: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
        },
      },
      {
        name: 'Okta',
        type: 'okta',
        description: 'Okta Identity Provider',
        entityIdTemplate: 'http://www.okta.com/{app-id}',
        ssoServiceUrlTemplate: 'https://{org}.okta.com/app/{app-id}/sso/saml',
        sloServiceUrlTemplate: 'https://{org}.okta.com/app/{app-id}/slo/saml',
        attributeMapping: {
          userId: 'NameID',
          email: 'email',
          firstName: 'firstName',
          lastName: 'lastName',
          role: 'role',
        },
      },
      {
        name: 'OneLogin',
        type: 'onelogin',
        description: 'OneLogin Identity Provider',
        entityIdTemplate: 'https://{subdomain}.onelogin.com/saml/metadata/{app-id}',
        ssoServiceUrlTemplate:
          'https://{subdomain}.onelogin.com/trust/saml2/http-post/sso/{app-id}',
        sloServiceUrlTemplate:
          'https://{subdomain}.onelogin.com/trust/saml2/http-redirect/slo/{app-id}',
        attributeMapping: {
          userId: 'NameID',
          email: 'User.email',
          firstName: 'User.FirstName',
          lastName: 'User.LastName',
          role: 'User.Role',
        },
      },
      {
        name: 'Ping Identity',
        type: 'ping-identity',
        description: 'Ping Identity Provider',
        entityIdTemplate: 'https://{server}/idp/shibboleth',
        ssoServiceUrlTemplate: 'https://{server}/idp/profile/SAML2/Redirect/SSO',
        sloServiceUrlTemplate: 'https://{server}/idp/profile/SAML2/Redirect/SLO',
        attributeMapping: {
          userId: 'NameID',
          email: 'mail',
          firstName: 'givenName',
          lastName: 'sn',
          role: 'memberOf',
        },
      },
    ];
  }
}

// Export singleton instance
const samlApi = new SAMLApi();
export default samlApi;
